<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Search, LayoutGrid, MessageSquare, ArrowRight } from 'lucide-svelte';
  import { searchApi } from '$lib/api/search';
  import { projectsApi } from '$lib/api/projects';
  import { palette } from '$lib/state/palette.svelte';
  import { route } from '$lib/state/route.svelte';

  let query = $state('');
  let index = $state(0);

  // Reset the input each time the palette opens (matches the prototype's
  // togglePalette resetting query/palIndex).
  $effect(() => {
    if (palette.open) {
      query = '';
      index = 0;
    }
  });

  const projectsQuery = createQuery(() => ({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    enabled: palette.open
  }));
  function projectName(id: number | null): string {
    if (id === null) return 'Inbox';
    return projectsQuery.data?.results.find((p) => p.id === id)?.name ?? `Project #${id}`;
  }

  const searchQuery = createQuery(() => ({
    queryKey: ['palette-search', query],
    queryFn: () => searchApi.query(query),
    enabled: palette.open
  }));

  type Item =
    | { type: 'task'; id: number; project_id: number; key: string; title: string }
    | { type: 'ticket'; id: number; project_id: number | null; title: string };

  const tasks = $derived((searchQuery.data?.tasks ?? []).map((t) => ({ type: 'task' as const, ...t })));
  const tickets = $derived((searchQuery.data?.tickets ?? []).map((t) => ({ type: 'ticket' as const, ...t })));
  const flat: Item[] = $derived([...tasks, ...tickets]);
  const clampedIndex = $derived(flat.length ? Math.min(index, flat.length - 1) : 0);

  function runItem(it: Item) {
    palette.close();
    if (it.type === 'task') route.push({ tab: 'project', projectId: it.project_id });
    else route.push({ tab: 'ticket', ticketId: it.id, back: route.current });
  }

  function onInputKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      index = Math.min(clampedIndex + 1, flat.length - 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      index = Math.max(clampedIndex - 1, 0);
    } else if (e.key === 'Enter') {
      if (flat[clampedIndex]) runItem(flat[clampedIndex]);
    }
  }

  // Esc closes the palette first (design pack §4 priority: palette -> modal
  // -> ...). Window-level so it works even if focus isn't in the input.
  function onWindowKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && palette.open) palette.close();
  }
</script>

<svelte:window onkeydown={onWindowKeydown} />

{#if palette.open}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="pal-overlay" role="presentation" onclick={() => palette.close()}>
    <div class="pal" role="dialog" aria-modal="true" tabindex="-1" onclick={(e) => e.stopPropagation()}>
      <div class="pal-in-wrap">
        <Search size={18} />
        <!-- svelte-ignore a11y_autofocus -->
        <input
          class="pal-input"
          autofocus
          placeholder="Search tasks and tickets…"
          bind:value={query}
          onkeydown={onInputKeydown}
        />
        <span class="kbd">Esc</span>
      </div>
      {#if flat.length}
        <div class="pal-list">
          {#if tasks.length}
            <div class="pal-sec">Tasks</div>
            {#each tasks as it (it.id)}
              {@const gi = flat.indexOf(it)}
              <div
                class="pal-item {gi === clampedIndex ? 'sel' : ''}"
                role="option"
                aria-selected={gi === clampedIndex}
                tabindex="-1"
                onmouseenter={() => (index = gi)}
                onclick={() => runItem(it)}
              >
                <span class="pal-ic"><LayoutGrid size={16} /></span>
                <div style="min-width:0;flex:1">
                  <div class="pal-tt">{it.title}</div>
                  <div class="pal-ct">{projectName(it.project_id)} · {it.key}</div>
                </div>
                <ArrowRight size={15} />
              </div>
            {/each}
          {/if}
          {#if tickets.length}
            <div class="pal-sec">Tickets</div>
            {#each tickets as it (it.id)}
              {@const gi = flat.indexOf(it)}
              <div
                class="pal-item {gi === clampedIndex ? 'sel' : ''}"
                role="option"
                aria-selected={gi === clampedIndex}
                tabindex="-1"
                onmouseenter={() => (index = gi)}
                onclick={() => runItem(it)}
              >
                <span class="pal-ic"><MessageSquare size={16} /></span>
                <div style="min-width:0;flex:1">
                  <div class="pal-tt">{it.title}</div>
                  <div class="pal-ct">{projectName(it.project_id)}</div>
                </div>
                <ArrowRight size={15} />
              </div>
            {/each}
          {/if}
        </div>
      {:else}
        <div class="pal-empty">{query ? 'No matches in your projects.' : 'Type to search your tasks and tickets.'}</div>
      {/if}
    </div>
  </div>
{/if}
